import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../config';
import AdminLayout from './AdminLayout';
import './Admin.css';

function TopicManagement() {
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [categories, setCategories] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingTopic, setEditingTopic] = useState(null);
    const [formData, setFormData] = useState({
        canonicalName: '',
        category: '',
        subcategory: '',
        keywords: '',
        aliases: '',
        status: 'active'
    });
    const { currentUser } = useAuth();

    useEffect(() => {
        fetchTopics();
        fetchCategories();
    }, [filterCategory]);

    const fetchTopics = async () => {
        try {
            setLoading(true);
            setError(null);

            if (!currentUser) {
                throw new Error('Not authenticated');
            }

            const token = await currentUser.getIdToken();
            const params = new URLSearchParams({
                ...(searchQuery && { search: searchQuery }),
                ...(filterCategory !== 'all' && { category: filterCategory })
            });

            const response = await fetch(`${API_URL}/api/topics/search?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Server error: ${response.status}`);
            }

            const data = await response.json();
            if (data.success) {
                setTopics(data.topics || []);
            } else {
                setTopics([]);
            }
        } catch (err) {
            console.error('Topics fetch error:', err);
            setError(err.message);
            setTopics([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await fetch(`${API_URL}/api/topics/categories`);
            const data = await response.json();
            if (data.success) {
                setCategories(data.categories || []);
            }
        } catch (err) {
            console.error('Failed to fetch categories:', err);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchTopics();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // In a full implementation, this would save to the backend
        alert('Topic management save functionality will be connected to the backend');
        setShowAddModal(false);
        setEditingTopic(null);
        resetForm();
    };

    const resetForm = () => {
        setFormData({
            canonicalName: '',
            category: '',
            subcategory: '',
            keywords: '',
            aliases: '',
            status: 'active'
        });
    };

    const openEditModal = (topic) => {
        setEditingTopic(topic);
        setFormData({
            canonicalName: topic.canonicalName || topic.name || '',
            category: topic.category || '',
            subcategory: topic.subcategory || '',
            keywords: (topic.keywords || []).join(', '),
            aliases: (topic.aliases || []).join(', '),
            status: topic.status || 'active'
        });
        setShowAddModal(true);
    };

    const getStatusBadge = (status) => {
        const badges = {
            active: <span className="badge badge-success">Active</span>,
            pending: <span className="badge badge-warning">Pending</span>,
            inactive: <span className="badge badge-secondary">Inactive</span>
        };
        return badges[status] || <span className="badge">{status}</span>;
    };

    return (
        <AdminLayout>
            <div className="admin-topics">
                <div className="admin-header">
                    <h1>Topic Library</h1>
                    <button className="add-btn" onClick={() => setShowAddModal(true)}>
                        + Add Topic
                    </button>
                </div>

                {/* Filters */}
                <div className="filters-bar">
                    <form onSubmit={handleSearch} className="search-form">
                        <input
                            type="text"
                            placeholder="Search topics..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input"
                        />
                        <button type="submit" className="search-btn">Search</button>
                    </form>

                    <select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Categories</option>
                        {categories.map(cat => (
                            <option key={cat.id || cat.name} value={cat.id || cat.name}>
                                {cat.name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Topics Table */}
                {loading ? (
                    <div className="admin-loading-container">
                        <div className="spinner"></div>
                        <p>Loading topics...</p>
                    </div>
                ) : error ? (
                    <div className="admin-error">
                        <p>{error}</p>
                        <button onClick={fetchTopics}>Retry</button>
                    </div>
                ) : topics.length === 0 ? (
                    <div className="empty-state">
                        <span className="empty-icon">🏷️</span>
                        <h3>No topics found</h3>
                        <p>Add canonical topics to help normalize user inputs.</p>
                        <button className="add-btn" onClick={() => setShowAddModal(true)}>
                            Add First Topic
                        </button>
                    </div>
                ) : (
                    <div className="table-container">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Topic Name</th>
                                    <th>Category</th>
                                    <th>Keywords</th>
                                    <th>Popularity</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topics.map(topic => (
                                    <tr key={topic.id || topic.canonicalName}>
                                        <td>
                                            <div className="topic-name-cell">
                                                <span className="topic-name">{topic.canonicalName || topic.name}</span>
                                                {topic.aliases && topic.aliases.length > 0 && (
                                                    <span className="topic-aliases">
                                                        Also: {topic.aliases.slice(0, 3).join(', ')}
                                                        {topic.aliases.length > 3 && '...'}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="category-badge">{topic.category || 'Uncategorized'}</span>
                                            {topic.subcategory && (
                                                <span className="subcategory"> / {topic.subcategory}</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="keywords-cell">
                                                {(topic.keywords || []).slice(0, 5).map((kw, idx) => (
                                                    <span key={idx} className="keyword-tag">{kw}</span>
                                                ))}
                                                {(topic.keywords || []).length > 5 && (
                                                    <span className="keyword-more">+{topic.keywords.length - 5}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="popularity-bar">
                                                <div
                                                    className="popularity-fill"
                                                    style={{ width: `${Math.min((topic.popularity || 0) * 10, 100)}%` }}
                                                ></div>
                                            </div>
                                            <span className="popularity-count">{topic.popularity || 0}</span>
                                        </td>
                                        <td>{getStatusBadge(topic.status || 'active')}</td>
                                        <td>
                                            <button
                                                className="action-btn view"
                                                onClick={() => openEditModal(topic)}
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Add/Edit Topic Modal */}
                {showAddModal && (
                    <div className="modal-overlay" onClick={() => { setShowAddModal(false); resetForm(); }}>
                        <div className="modal-content" onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>{editingTopic ? 'Edit Topic' : 'Add New Topic'}</h2>
                                <button className="close-btn" onClick={() => { setShowAddModal(false); resetForm(); }}>×</button>
                            </div>

                            <form onSubmit={handleSubmit} className="topic-form">
                                <div className="form-group">
                                    <label>Canonical Name *</label>
                                    <input
                                        type="text"
                                        value={formData.canonicalName}
                                        onChange={(e) => setFormData({ ...formData, canonicalName: e.target.value })}
                                        placeholder="e.g., Golf, Python Programming"
                                        required
                                    />
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Category *</label>
                                        <select
                                            value={formData.category}
                                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                            required
                                        >
                                            <option value="">Select category</option>
                                            {categories.map(cat => (
                                                <option key={cat.id || cat.name} value={cat.id || cat.name}>
                                                    {cat.name}
                                                </option>
                                            ))}
                                            <option value="other">Other</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Subcategory</label>
                                        <input
                                            type="text"
                                            value={formData.subcategory}
                                            onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                                            placeholder="e.g., Sports, Web Development"
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Keywords (comma-separated)</label>
                                    <textarea
                                        value={formData.keywords}
                                        onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                                        placeholder="scratch golfer, handicap, swing, putting"
                                        rows={3}
                                    />
                                    <span className="form-hint">These help match user inputs to this topic</span>
                                </div>

                                <div className="form-group">
                                    <label>Aliases (comma-separated)</label>
                                    <textarea
                                        value={formData.aliases}
                                        onChange={(e) => setFormData({ ...formData, aliases: e.target.value })}
                                        placeholder="golfing, golf basics, learn to golf"
                                        rows={2}
                                    />
                                    <span className="form-hint">Alternative names for this topic</span>
                                </div>

                                <div className="form-group">
                                    <label>Status</label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    >
                                        <option value="active">Active</option>
                                        <option value="pending">Pending Review</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>

                                <div className="modal-actions">
                                    <button type="button" className="action-btn secondary" onClick={() => { setShowAddModal(false); resetForm(); }}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="action-btn primary">
                                        {editingTopic ? 'Save Changes' : 'Add Topic'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}

export default TopicManagement;
